/**
 * PerformanceSummaryText Component
 * Brief AI-style performance summary text
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../common';
import { Quote } from 'lucide-react';

export interface PerformanceSummaryTextProps {
  summary: string;
  userName?: string;
  periodLabel?: string;
  className?: string;
  isLoading?: boolean;
}

export const PerformanceSummaryText: React.FC<PerformanceSummaryTextProps> = ({
  summary,
  // userName is available for future use in personalized summaries
  periodLabel,
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-40 mb-4" />
          <div className="h-4 bg-stone-200 rounded w-full mb-2" />
          <div className="h-4 bg-stone-200 rounded w-3/4" />
        </div>
      </Card>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <Card className={clsx('p-6 bg-gradient-to-br from-blue-50 to-indigo-50', className)}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Quote className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-stone-800">
              Performance Summary
            </h3>
            {periodLabel && (
              <span className="text-sm text-stone-500">{periodLabel}</span>
            )}
          </div>

          {/* Summary text */}
          <p className="text-stone-700 leading-relaxed">{summary}</p>

          {/* Attribution */}
          <p className="mt-3 text-xs text-stone-400">
            Auto-generated based on performance metrics
          </p>
        </div>
      </div>
    </Card>
  );
};

export default PerformanceSummaryText;
